import RequestBuilder, { comparisonTypes } from 'Resources/RequestBuilder';
import T from 'Components/Helpers/translate';
import { MultiAction } from 'App/components/ListViewActions';
import { MultiSelect } from 'App/components/ListView/ListViewRenderHelpers';
import Attribute from 'App/babel/attributes/Attribute';
import updateContacts from './updateContacts';
import updateCompany from './updateCompany';
import updateOrder from './updateOrder';
import archiveTickets from './archiveTickets';
import restoreTickets from './restoreTickets';
import updateTickets from './updateTickets';
import removePromise from './removePromise';
import planPhonecalls from './planPhonecalls';
import createActivity from './createActivity';
import createMailCampaign from './createMailCampaign';
import anonymizeContacts from './anonymizeContacts';
import resetScore from './resetScore';
import sendMail from './sendMail';
import sendMailFromOrders from './sendMailFromOrders';
import addContactsToSegment from './addContactsToSegment';
import addLabel from './addLabel';
import removeLabel from './removeLabel';
import { IconName } from '@upsales/components';
import updateActivities from './updateActivities';
import { MultiSelectContext } from 'App/components/MultiselectProvider/MultiselectProvider';
import { Feature, isAvailable, hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import scrapeCompanyWebsites from './scrapeCompanyWebsites';
import { ListView, ListViewFilter } from 'App/resources/AllIWant';
import closeActivities from './closeActivities';
import updateAppointments from './updateAppointments';
import updateTasks from './updateTasks';
import updateProjectPlan from './updateProjectPlan';
import openModal from 'App/services/Modal';
import mergeTickets from './mergeTickets';
import planTodos from './planTodos';
import inactivateCompanies from './inactivateCompanies';
import toggleContactsActive from './toggleContactsActive';
import prospectingRefresh from './prospectingRefresh';
import bookCustomerVisits from './bookCustomerVisits';
import { offersBookVisits } from 'App/helpers/visits/visitTypes';
import store from 'App/babel/store';
import {
	checkContactScraperRoleAccess,
	hasContactScraperAccountAccess
} from 'App/components/ContactScraper/useContactScraperAccess';
import AppService from 'App/legacy/AppService';
import { SEGMENT_MAX_CONTACTS } from 'App/helpers/segmentAccess';
import { campaignSelectionFromFilters } from './campaignSelection';

const hasContactScraperAccess = (): boolean => {
	// WEB-10512: addon OR feature. The addon arm is org-wide and unfiltered, so it keeps
	// non-crm users who can run this today; the feature arm covers the Upsales AI tier,
	// which is no longer sold the addon and so has no receipt. WEB-10743: read it from the
	// shared helper so this can't drift from the gate the API applies — it spelled the
	// feature arm as `aiContactScraper` while `canUseContactScraper` reads `aiAssistant`.
	if (!hasContactScraperAccountAccess()) {
		return false;
	}

	if (!store) {
		return false;
	}

	const state = store.getState();
	return checkContactScraperRoleAccess({
		configString: state.App.metadata?.params?.ContactScraperRoleFilter as string | undefined,
		isAdmin: !!state.App.self?.administrator,
		userRoleId: state.App.self?.role?.id
	});
};

export type { MultiAction };

export enum MultiActions {
	UPDATE_APPOINTMENT = 'updateAppointment',
	CLOSE_ACTIVITY = 'closeActivity',
	UPDATE_ACTIVITY = 'updateActivity',
	UPDATE_CONTACT = 'updateContact',
	UPDATE_CONTACTS = 'updateContacts',
	UPDATE_CLIENT = 'updateClient',
	UPDATE_ORDER = 'updateOrder',
	UPDATE_OPPORTUNITY = 'updateOpportunity',
	SCRAPE_CLIENT_WEBPAGE = 'scrapeClientWebpage',
	CREATE_ACTIVITY = 'createActivity',
	CREATE_MAIL_CAMPAIGN = 'createMailCampaign',
	PLAN_PHONECALL = 'planPhonecall',
	CREATE_TODO = 'planTodos',
	BOOK_VISITS = 'bookVisits',
	UPDATE_PHONECALL = 'phonecall',
	UPDATE_TODO = 'todo',
	EXPORT = 'export',
	REMOVE = 'remove',
	ANONYMIZE = 'anonymize',
	SEND_MAIL = 'sendMail',
	ADD_CONTACTS_TO_SEGMENT = 'addContactsToSegment',
	RESET_SCORE = 'resetScore',
	ADD_LABEL = 'addLabel',
	REMOVE_LABEL = 'removeLabel',
	ARCHIVE_TICKETS = 'archiveTickets',
	RESTORE_TICKETS = 'restoreTickets',
	MERGE_TICKETS = 'mergeTickets',
	UPDATE_TICKETS = 'updateTickets',
	INACTIVATE_COMPANIES = 'inactivateCompanies',
	TOGGLE_CONTACTS_ACTIVE = 'toggleContactsActive',
	PROSPECTING_REFRESH = 'prospectingRefresh',
	UPDATE_PROJECT_PLAN = 'updateProjectPlan',
	UPDATE_USER_DEFINED_OBJECTS = 'updateUserDefinedObjects'
}

type BaseMultiAction = { id: MultiActions; title: string; icon: IconName; available?: boolean };
type DisableableMultiAction = BaseMultiAction & {
	disabled: boolean | ((selected: number, allSelected: boolean) => boolean);
	disabledTooltip: string;
};
type MultiActionDefinition = BaseMultiAction | DisableableMultiAction;

export const getMultiAction = (
	action: MultiActions,
	hasExportRights: boolean,
	entity: string
): MultiActionDefinition => {
	const hasTodoList = hasSoftDeployAccess('TODO_LIST');
	const hasRemoveActivities = hasSoftDeployAccess('REMOVE_ACTIVITIES');
	const hasActivitiesAndAppointments = isAvailable(Feature.ACTIVITIES_AND_APPOINTMENTS);
	const hasGroupMail = isAvailable(Feature.GROUP_MAIL);
	const hasEmail = isAvailable(Feature.EMAIL);
	const hasSegment = isAvailable(Feature.SEGMENT);
	const hasLeads = isAvailable(Feature.LEADS);
	const hasProspectingBasic = isAvailable(Feature.PROSPECTING_BASIC);
	switch (action) {
		case MultiActions.UPDATE_APPOINTMENT:
			return {
				id: MultiActions.UPDATE_APPOINTMENT,
				title: T('default.update') + ' ' + T('default.appointments').toLowerCase(),
				icon: 'check-square-o'
			};
		case MultiActions.CLOSE_ACTIVITY:
			return { id: MultiActions.CLOSE_ACTIVITY, title: 'default.closeActivities', icon: 'check-square-o' };
		case MultiActions.UPDATE_PHONECALL:
			return {
				id: MultiActions.UPDATE_PHONECALL,
				title: T('default.update') + ' ' + T('default.phonecalls').toLowerCase(),
				icon: 'edit'
			};
		case MultiActions.UPDATE_TODO:
			return {
				id: MultiActions.UPDATE_TODO,
				title: T('default.update') + ' ' + T('default.todos').toLowerCase(),
				icon: 'edit'
			};
		case MultiActions.UPDATE_ACTIVITY:
			return {
				id: MultiActions.UPDATE_ACTIVITY,
				title: T('default.update') + ' ' + T('default.activities').toLowerCase(),
				icon: 'edit'
			};
		case MultiActions.UPDATE_CONTACT:
			return { id: MultiActions.UPDATE_CONTACT, title: 'default.updateContacts', icon: 'user' };
		case MultiActions.UPDATE_CONTACTS:
			return {
				id: MultiActions.UPDATE_CONTACTS,
				title: T('default.updateClientContacts'),
				icon: 'user'
			};
		case MultiActions.SCRAPE_CLIENT_WEBPAGE: {
			return {
				id: MultiActions.SCRAPE_CLIENT_WEBPAGE,
				title: T('default.scrapeClientsWebpage'),
				icon: 'stars',
				available: hasContactScraperAccess()
			};
		}
		case MultiActions.UPDATE_CLIENT:
			return { id: MultiActions.UPDATE_CLIENT, title: 'default.updateAccounts', icon: 'home' };
		case MultiActions.UPDATE_ORDER:
			return {
				id: MultiActions.UPDATE_ORDER,
				title: 'default.updateOrders',
				icon: 'dollar'
			};
		case MultiActions.UPDATE_OPPORTUNITY:
			return {
				id: MultiActions.UPDATE_ORDER,
				title: 'default.updateOpportunities',
				icon: 'dollar'
			};
		case MultiActions.PLAN_PHONECALL:
			return {
				id: MultiActions.PLAN_PHONECALL,
				title: 'todo.planPhonecalls',
				available: hasTodoList,
				icon: 'phone'
			};
		case MultiActions.CREATE_TODO:
			return {
				id: MultiActions.CREATE_TODO,
				title: 'todo.createTodo',
				available: hasTodoList,
				icon: 'check-square-o'
			};
		case MultiActions.CREATE_ACTIVITY:
			return {
				id: MultiActions.CREATE_ACTIVITY,
				title: 'accountGrowth.quickCreateActivities',
				available: hasActivitiesAndAppointments && !(hasRemoveActivities && hasTodoList),
				icon: 'activity'
			};
		case MultiActions.BOOK_VISITS: {
			const self = AppService.getSelf();
			return {
				id: MultiActions.BOOK_VISITS,
				title: 'listView.bookVisits.title',
				icon: 'calendar',
				available: offersBookVisits({
					canCreateAppointments: !!self?.crm && hasActivitiesAndAppointments,
					appointmentRight: self?.createRights?.Appointment,
					administrator: !!self?.administrator,
					appointmentTypes: AppService.getActivityTypes('appointment', true)
				})
			};
		}
		case MultiActions.EXPORT:
			return {
				id: MultiActions.EXPORT,
				title: 'default.export',
				icon: 'table',
				disabled: !hasExportRights,
				disabledTooltip: 'default.noExportRights'
			};
		case MultiActions.REMOVE:
			return { id: MultiActions.REMOVE, title: 'default.delete', icon: 'trash' };
		case MultiActions.CREATE_MAIL_CAMPAIGN:
			return {
				id: MultiActions.CREATE_MAIL_CAMPAIGN,
				title: 'default.sendGroupMail',
				icon: 'paper-plane',
				disabledTooltip: T('mailCampaign.cantCreate'),
				disabled: (selected: number, allSelected: boolean) =>
					(entity === 'mail' && allSelected) || selected > 1000,
				available: hasGroupMail
			};
		case MultiActions.ANONYMIZE:
			return {
				id: MultiActions.ANONYMIZE,
				title: 'admin.anonymization.startProcess',
				icon: 'fa-user-secret' as IconName,
				available: AppService.getSelf().administrator
			};
		case MultiActions.SEND_MAIL:
			return {
				id: MultiActions.SEND_MAIL,
				title: 'default.sendEmail',
				icon: 'fa-envelope' as IconName,
				disabledTooltip: 'multi.sendMail.maxContacts',
				available: hasEmail,
				disabled: selected => selected > 100
			};
		case MultiActions.ADD_CONTACTS_TO_SEGMENT:
			return {
				id: MultiActions.ADD_CONTACTS_TO_SEGMENT,
				title: 'default.addToSegment',
				icon: 'fa-code-fork' as IconName,
				disabledTooltip: 'multi.addContactsToSegment.maxContacts',
				disabled: selected => selected > SEGMENT_MAX_CONTACTS,
				available: hasSegment
			};
		case MultiActions.RESET_SCORE:
			return {
				id: MultiActions.RESET_SCORE,
				title: 'default.resetScore',
				icon: 'thumbs-down',
				available: hasLeads
			};
		case MultiActions.ADD_LABEL:
			return { id: MultiActions.ADD_LABEL, title: 'file.addLabel', icon: 'tag' };
		case MultiActions.REMOVE_LABEL:
			return { id: MultiActions.REMOVE_LABEL, title: 'file.label.remove', icon: 'minus-square' };
		case MultiActions.ARCHIVE_TICKETS:
			return { id: MultiActions.ARCHIVE_TICKETS, title: 'default.archive', icon: 'archive' };
		case MultiActions.RESTORE_TICKETS:
			return { id: MultiActions.RESTORE_TICKETS, title: 'default.restoreFromArchive', icon: 'undo' };
		case MultiActions.MERGE_TICKETS:
			return { id: MultiActions.MERGE_TICKETS, title: 'default.merge.tickets', icon: 'merge' };
		case MultiActions.UPDATE_TICKETS:
			return {
				id: MultiActions.UPDATE_TICKETS,
				title: 'default.update.tickets',
				icon: 'customer-support'
			};
		case MultiActions.INACTIVATE_COMPANIES:
			return {
				id: MultiActions.INACTIVATE_COMPANIES,
				title: 'default.inactivateCompanies',
				icon: 'times-circle'
			};
		case MultiActions.TOGGLE_CONTACTS_ACTIVE:
			return {
				id: MultiActions.TOGGLE_CONTACTS_ACTIVE,
				title: 'default.toggleContactsActive',
				icon: 'exchange'
			};
		case MultiActions.PROSPECTING_REFRESH:
			return {
				id: MultiActions.PROSPECTING_REFRESH,
				title: 'multi.prospectingRefresh',
				icon: 'refresh',
				available: hasProspectingBasic
			};
		case MultiActions.UPDATE_PROJECT_PLAN:
			return {
				id: MultiActions.UPDATE_PROJECT_PLAN,
				title: 'projectPlan.updateProjects',
				icon: 'edit'
			};
		case MultiActions.UPDATE_USER_DEFINED_OBJECTS:
			return {
				id: MultiActions.UPDATE_USER_DEFINED_OBJECTS,
				title: 'default.updateObjects',
				icon: 'edit',
				available: hasSoftDeployAccess('MULTI_UPDATE_USER_DEFINED_OBJECTS')
			};
		default:
			throw new Error('multiActionRunnerHelpers.getMultiAction - Action not found: ' + action);
	}
};

export const getMultiActions = (actions: MultiActions[], hasExportRights = false, entity: string) =>
	actions
		.map(action => getMultiAction(action, hasExportRights, entity))
		.filter(action => action.available || typeof action.available !== 'boolean');

export type FilterGet = (rb: RequestBuilder, multiselect: MultiSelect) => RequestBuilder;

const idAttribute = Attribute({
	title: 'default.id',
	field: 'id',
	type: Attribute.types.Number
});

export const defaultFilterGetter: FilterGet = (requestBuilder, multiselect) => {
	if (multiselect.allSelected || multiselect.allSelectedFilter) {
		if (multiselect.deselectedIds?.length) {
			requestBuilder.addFilter(idAttribute, comparisonTypes.NotEquals, multiselect.deselectedIds);
		}
		return requestBuilder;
	}
	const rb = new RequestBuilder();
	rb.addFilter(idAttribute, comparisonTypes.Equals, multiselect.selectedIds);
	return rb;
};

type RunMultiActionType = {
	action: MultiAction;
	provided: {
		requestBuilder: RequestBuilder | null;
		selectedView: ListView | null;
		filters: { [filterName: string]: ListViewFilter };
	};
	multiselect: MultiSelectContext;
	getFilters?: FilterGet;
	entityType: string;
	notifyEntity: string;
	extraParams?: { [key: string]: any };
	options?: { [key: string]: any };
	name?: string | null;
	justExport?: boolean;
	notificationEntityNameExtension?: string;
	/** Set only where provided.filters are the list's whole query, so a campaign can reopen the run's selection. */
	filtersDescribeList?: boolean;
};

export const runMultiAction = ({
	action,
	provided,
	multiselect,
	entityType,
	notifyEntity,
	getFilters = defaultFilterGetter,
	extraParams = undefined,
	options,
	name = null,
	justExport = true,
	notificationEntityNameExtension = undefined,
	filtersDescribeList = false
}: RunMultiActionType) => {
	const { requestBuilder, selectedView, filters } = provided;
	if (!requestBuilder) {
		return;
	}
	const campaignSelection =
		filtersDescribeList && multiselect.allSelected && !multiselect.deselectedIds?.length
			? campaignSelectionFromFilters(filters, selectedView?.type, AppService.getSelf()?.id)
			: undefined;

	switch (action.id) {
		case MultiActions.UPDATE_APPOINTMENT:
			return updateAppointments(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.CLOSE_ACTIVITY:
			return closeActivities(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.UPDATE_PHONECALL:
			return updateTasks(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.UPDATE_TODO:
			return updateTasks(getFilters(requestBuilder, multiselect), entityType, multiselect, true);
		case MultiActions.UPDATE_ACTIVITY:
			return updateActivities(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.UPDATE_CONTACT:
			return updateContacts(
				getFilters(requestBuilder, multiselect),
				false,
				entityType,
				extraParams,
				multiselect,
				campaignSelection
			);
		case MultiActions.UPDATE_CONTACTS:
			return updateContacts(
				getFilters(requestBuilder, multiselect),
				true,
				entityType,
				extraParams,
				multiselect,
				campaignSelection
			);
		case MultiActions.UPDATE_CLIENT:
			return updateCompany(
				getFilters(requestBuilder, multiselect),
				entityType,
				extraParams,
				multiselect,
				campaignSelection
			);
		case MultiActions.CREATE_ACTIVITY:
			return createActivity(
				getFilters(requestBuilder, multiselect),
				entityType,
				name,
				extraParams,
				multiselect.selected
			);
		case MultiActions.PLAN_PHONECALL:
			return planPhonecalls(
				getFilters(requestBuilder, multiselect),
				entityType,
				multiselect.selected,
				multiselect.selectedIds
			);
		case MultiActions.CREATE_TODO:
			return planTodos(
				getFilters(requestBuilder, multiselect),
				entityType,
				multiselect.selected,
				multiselect.selectedIds,
				{
					isMultiCreate: true
				}
			);
		case MultiActions.BOOK_VISITS:
			return bookCustomerVisits(
				getFilters(requestBuilder.clone(), multiselect),
				requestBuilder,
				entityType === 'Contact' ? 'contact' : 'client',
				multiselect.selected
			);
		case MultiActions.EXPORT: {
			const exportType = notifyEntity === 'accounts' ? 'accounts' : entityType.toLowerCase();
			// Fields should not have any values at this point, because we do not yet know which fields will be chosen
			// Without this some fields would be empty when default exporting in AccountGrowth
			requestBuilder.fields = [];

			const exportFilters = getFilters(requestBuilder, multiselect).build();
			if (exportFilters.usedForProspectingMonitor !== undefined) {
				delete exportFilters.usedForProspectingMonitor;
			}

			return openModal('ExportDataReact', {
				justExport,
				columns: selectedView?.columns || [],
				optionsByType: true,
				filters: exportFilters,
				entity: entityType,
				exportType: exportType,
				sort: { attribute: 'id', ascending: true },
				options,
				selectedIds: multiselect.selectedIds,
				allSelected: multiselect.allSelected,
				includes: extraParams?.includes
			});
		}
		case MultiActions.REMOVE:
			return removePromise(
				getFilters(requestBuilder, multiselect),
				entityType,
				notifyEntity,
				name,
				multiselect.allSelected ? [] : multiselect.selectedIds,
				multiselect.selected,
				notificationEntityNameExtension
			);
		case MultiActions.UPDATE_ORDER:
			return updateOrder(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.CREATE_MAIL_CAMPAIGN:
			return createMailCampaign(filters, multiselect.allSelected, multiselect.selectedIds, entityType);
		case MultiActions.SEND_MAIL:
			if (entityType === 'Order' || entityType === 'Opportunity') {
				return sendMailFromOrders(getFilters(requestBuilder, multiselect));
			}
			return sendMail(getFilters(requestBuilder, multiselect));
		case MultiActions.ANONYMIZE:
			return anonymizeContacts(requestBuilder, multiselect);
		case MultiActions.ADD_CONTACTS_TO_SEGMENT:
			return addContactsToSegment(
				getFilters(requestBuilder, multiselect),
				multiselect.allSelected,
				multiselect.selectedIds
			);
		case MultiActions.RESET_SCORE: {
			return resetScore(getFilters(requestBuilder, multiselect), entityType, multiselect, extraParams);
		}
		case MultiActions.ADD_LABEL: {
			return addLabel(getFilters(requestBuilder, multiselect), entityType, multiselect.selected);
		}
		case MultiActions.REMOVE_LABEL: {
			return removeLabel(getFilters(requestBuilder, multiselect), entityType, multiselect.selected);
		}
		case MultiActions.SCRAPE_CLIENT_WEBPAGE:
			return scrapeCompanyWebsites(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.ARCHIVE_TICKETS:
			return archiveTickets(getFilters(requestBuilder, multiselect), entityType, multiselect.selected);
		case MultiActions.RESTORE_TICKETS:
			return restoreTickets(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.MERGE_TICKETS:
			return mergeTickets(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.UPDATE_TICKETS:
			return updateTickets(getFilters(requestBuilder, multiselect), entityType, multiselect);
		case MultiActions.INACTIVATE_COMPANIES: {
			const customerId = store.getState().App.customerId;
			return inactivateCompanies(getFilters(requestBuilder, multiselect), entityType, multiselect, customerId);
		}
		case MultiActions.TOGGLE_CONTACTS_ACTIVE: {
			const customerId = store.getState().App.customerId;
			return toggleContactsActive(getFilters(requestBuilder, multiselect), entityType, multiselect, customerId);
		}
		case MultiActions.PROSPECTING_REFRESH: {
			const customerId = store.getState().App.customerId;
			return prospectingRefresh(getFilters(requestBuilder, multiselect), entityType, multiselect, customerId);
		}
		case MultiActions.UPDATE_PROJECT_PLAN:
			return updateProjectPlan(getFilters(requestBuilder, multiselect), entityType, multiselect);
	}
};
